import PostBusiness from '@/components/post-business/PostBusiness'
import PostActions from '@/components/post-actions/PostActions'
import EventDates from '@/components/event-dates/EventDates'
import Button from '@/components/button/Button'

import { BasicCountdownTimer } from '@/components/grid-archive/CountdownTimer'

import type { HeroSubDetailsProps } from '@/components/hero/types'

// TODO: more granular control over buttons/share links
export default function HeroSubDetails({
  postId = null,
  postType = null,
  contentType = null,
  businessId = null,
  publishDate = null,
  lastModified = null,
  showLastModified = null,
  eventDates = null,
  timezone = null,
  timezoneModal = null,
  location = null,
  button = null,
  postActions = undefined,
  countdownDate = null,
  countdownLabel = null,
}: HeroSubDetailsProps) {
  const showPublishDate = postType && ['all-news', 'all-work'].includes(postType)

  let postActionSettings = postActions

  // Default to showing share links for news, work and events
  if (
    postActions === undefined &&
    postType &&
    ['all-news', 'all-work', 'all-events'].includes(postType)
  ) {
    postActionSettings = {
      share: true,
      download: true,
      print: true,
      bookmark: true,
    }
  }

  const typeIcon = contentType?.slug ? (
    <svg className="hidden h-[24px] w-[24px]" aria-hidden="true">
      <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-${contentType?.slug}`}></use>
    </svg>
  ) : null

  const postBusiness = businessId ? <PostBusiness businessId={businessId} /> : null

  const authorHTML =
    postBusiness || (publishDate && showPublishDate) ? (
      <div className="mb-0 flex flex-wrap gap-x-xxs">
        {typeIcon}
        {postBusiness ? postBusiness : null}
        {postBusiness && publishDate && showPublishDate ? <span>|</span> : null}

        {showLastModified && lastModified ? (
          <>
            <span>{lastModified}</span>
            {publishDate && showPublishDate ? (
              <span className="w-full text-fg/70">(Originally published: {publishDate})</span>
            ) : null}
          </>
        ) : (
          <>{publishDate && showPublishDate ? <span>{publishDate}</span> : null}</>
        )}
      </div>
    ) : null

  const locationHTML = location ? (
    <div className="flex items-center gap-xxs">
      <svg className="h-[24px] w-[24px]" aria-hidden="true">
        <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-location`}></use>
      </svg>
      <span>{location}</span>
    </div>
  ) : null

  const eventDateHTML =
    postType === 'all-events' && eventDates ? (
      <div className="flex items-start gap-xxs">
        <svg className="h-[24px] w-[24px]" aria-hidden="true">
          <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-calendar`}></use>
        </svg>

        <EventDates
          dates={eventDates}
          timezone={timezone}
          timezoneModal={timezoneModal}
          full={true}
        />
      </div>
    ) : null

  const countdownHTML = countdownDate ? (
    <BasicCountdownTimer countdownDate={countdownDate} countdownLabel={countdownLabel} />
  ) : null

  if (
    !authorHTML &&
    !eventDateHTML &&
    !locationHTML &&
    !button?.url &&
    !postActions &&
    postActionSettings === undefined
  ) {
    return null
  }

  return (
    <div className="flex w-full flex-col items-start gap-l @container/subdetails">
      {countdownHTML}

      {authorHTML || eventDateHTML || locationHTML || postActionSettings ? (
        <div
          className={`flex w-full flex-col gap-s @[640px]/subdetails:flex-row @[640px]/subdetails:items-center @[640px]/subdetails:justify-between @[640px]/subdetails:gap-m`}>
          {authorHTML || eventDateHTML || locationHTML ? (
            <div className={`p--small mb-0 flex flex-col gap-y-xxs`}>
              {authorHTML}

              {eventDateHTML}

              {locationHTML}
            </div>
          ) : null}

          {postActionSettings ? <PostActions actions={postActionSettings} postId={postId} /> : null}
        </div>
      ) : null}

      {button?.url ? (
        <Button href={button.url} target={button.target ?? '_self'} className="btn btn--solid">
          {button.title}
        </Button>
      ) : null}
    </div>
  )
}
